import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { occasionsService } from '../../../services/occasions';

const OccasionsSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [upcomingOccasions, setUpcomingOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock data for non-logged-in users
  const mockOccasions = [
    {
      id: 1,
      title: "Sarah's 28th Birthday",
      date: "December 15, 2024",
      daysLeft: 8,
      relationship: "Sister",
      type: "birthday",
      icon: "Cake",
      color: "primary",
      suggestions: 3,
      budget: "$50-100",
      notes: "Loves photography and coffee"
    },
    {
      id: 2,
      title: "Wedding Anniversary",
      date: "December 22, 2024",
      daysLeft: 15,
      relationship: "Spouse",
      type: "anniversary",
      icon: "Heart",
      color: "secondary",
      suggestions: 5,
      budget: "$100-200",
      notes: "5 years together"
    },
    {
      id: 3,
      title: "Christmas Celebration",
      date: "December 25, 2024",
      daysLeft: 18,
      relationship: "Family",
      type: "holiday",
      icon: "Gift",
      color: "success",
      suggestions: 12,
      budget: "$200-500",
      notes: "Multiple family members"
    },
    {
      id: 4,
      title: "New Year's Party",
      date: "January 1, 2025",
      daysLeft: 25,
      relationship: "Friends",
      type: "celebration",
      icon: "Sparkles",
      color: "warning",
      suggestions: 7,
      budget: "$30-80",
      notes: "Group celebration"
    }
  ];

  const occasionTypes = [
    { name: "Birthdays", icon: "Cake", count: 12, color: "primary" },
    { name: "Anniversaries", icon: "Heart", count: 3, color: "secondary" },
    { name: "Holidays", icon: "Gift", count: 8, color: "success" },
    { name: "Celebrations", icon: "Sparkles", count: 15, color: "warning" }
  ];

  // Helper function to get icon based on occasion type
  const getOccasionIcon = (type) => {
    const iconMap = {
      'birthday': 'Cake',
      'anniversary': 'Heart',
      'wedding': 'Heart',
      'holiday': 'Gift',
      'christmas': 'Gift',
      'graduation': 'GraduationCap',
      'celebration': 'Sparkles',
      'new_year': 'Sparkles',
      'valentines': 'Heart',
      'mothers_day': 'Heart',
      'fathers_day': 'Heart'
    };
    return iconMap[type?.toLowerCase()] || 'Calendar';
  };

  // Helper function to get color based on occasion type
  const getOccasionColor = (type) => {
    const colorMap = {
      'birthday': 'primary',
      'anniversary': 'secondary',
      'wedding': 'secondary',
      'holiday': 'success',
      'christmas': 'success',
      'graduation': 'warning',
      'celebration': 'warning',
      'new_year': 'warning',
      'valentines': 'secondary',
      'mothers_day': 'secondary',
      'fathers_day': 'secondary'
    };
    return colorMap[type?.toLowerCase()] || 'primary';
  };

  // Helper function to calculate days left
  const calculateDaysLeft = (dateString) => {
    const today = new Date();
    const occasionDate = new Date(dateString);
    const diffTime = occasionDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // Helper function to format user occasions for display
  const formatUserOccasions = (occasions) => {
    return occasions.map(occasion => ({
      id: occasion.id,
      title: occasion.title || occasion.name,
      date: new Date(occasion.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      daysLeft: calculateDaysLeft(occasion.date),
      relationship: occasion.relationship || occasion.contact?.relationship || "Contact",
      type: occasion.occasion_type || occasion.category || "event",
      icon: getOccasionIcon(occasion.occasion_type || occasion.category),
      color: getOccasionColor(occasion.occasion_type || occasion.category),
      suggestions: occasion.suggestions_count || 0,
      budget: occasion.budget || "Not set",
      notes: occasion.notes || occasion.description || ""
    })).filter(occasion => occasion.daysLeft >= 0) // Only show future occasions
      .sort((a, b) => a.daysLeft - b.daysLeft); // Sort by days left
  };

  // Load occasions based on authentication status
  useEffect(() => {
    const loadOccasions = async () => {
      if (authLoading) return; // Wait for auth to load

      setLoading(true);
      setError(null);

      try {
        if (isAuthenticated) {
          // Fetch real user occasions
          const response = await occasionsService.getUpcomingOccasions(60); // Get occasions within 60 days
          const formattedOccasions = formatUserOccasions(response);

          // If user has no upcoming occasions, show mock data as examples
          if (formattedOccasions.length === 0) {
            setUpcomingOccasions(mockOccasions);
          } else {
            setUpcomingOccasions(formattedOccasions);
          }
        } else {
          // Show mock data for non-logged-in users
          setUpcomingOccasions(mockOccasions);
        }
      } catch (error) {
        console.error('Error loading occasions:', error);
        setError('Failed to load occasions');
        // Fallback to mock data on error
        setUpcomingOccasions(mockOccasions);
      } finally {
        setLoading(false);
      }
    };

    loadOccasions();
  }, [isAuthenticated, authLoading]);

  const handleOccasionClick = (occasion) => {
    if (isAuthenticated) {
      navigate('/gift-finder', { state: { occasion } });
    } else {
      // For non-logged-in users, redirect to login with return intent
      navigate('/login', { state: { from: '/gift-finder', occasion } });
    }
  };

  const handleViewAllOccasions = () => {
    if (isAuthenticated) {
      navigate('/occasions');
    } else {
      navigate('/login', { state: { from: '/occasions' } });
    }
  };

  // Calculate total number of slides needed (2 occasions per slide)
  const totalSlides = Math.ceil(upcomingOccasions.length / 2);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Get occasions for current slide (2 occasions per slide)
  const getCurrentSlideOccasions = () => {
    const startIndex = currentSlide * 2;
    return upcomingOccasions.slice(startIndex, startIndex + 2);
  };

  // Show loading state
  if (loading || authLoading) {
    return (
      <section className="py-16 lg:py-24 bg-gradient-to-br from-surface to-accent-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-accent-600">Loading occasions...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-surface to-accent-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-secondary-50 rounded-full px-4 py-2 mb-4">
            <Icon name="Calendar" size={16} className="text-secondary" />
            <span className="text-sm font-medium text-secondary">
              {isAuthenticated ? 'Your Occasions' : 'Example Occasions'}
            </span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-accent-800 mb-4">
            Never Miss a Special Moment
          </h2>
          <p className="text-lg text-accent-600 max-w-2xl mx-auto">
            {isAuthenticated
              ? 'Stay ahead of important dates with personalized reminders and curated gift suggestions'
              : 'Sign in to track your important dates with personalized reminders and curated gift suggestions'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Upcoming Occasions Carousel */}
          <div className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden">
            <div className="p-6 border-b border-accent-100">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-xl font-semibold text-accent-800">
                  Coming Up Next
                  {!isAuthenticated && (
                    <span className="text-sm font-normal text-accent-500 ml-2">(Examples)</span>
                  )}
                </h3>
                {totalSlides > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={prevSlide}
                      className="p-2 rounded-lg hover:bg-surface transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} className="text-accent-600" />
                    </button>
                    <span className="text-xs text-accent-500 px-2">
                      {currentSlide + 1} / {totalSlides}
                    </span>
                    <button
                      onClick={nextSlide}
                      className="p-2 rounded-lg hover:bg-surface transition-colors"
                    >
                      <Icon name="ChevronRight" size={16} className="text-accent-600" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6">
              {upcomingOccasions.length > 0 ? (
                <div className="space-y-4">
                  {getCurrentSlideOccasions().map((occasion, index) => (
                    <div
                      key={occasion.id}
                      className="flex items-start space-x-4 p-4 rounded-xl hover:bg-surface transition-colors group cursor-pointer"
                      onClick={() => handleOccasionClick(occasion)}
                    >
                      <div className={`flex-shrink-0 w-10 h-10 bg-${occasion.color}-100 rounded-lg flex items-center justify-center`}>
                        <Icon name={occasion.icon} size={20} className={`text-${occasion.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-medium text-accent-800 group-hover:text-primary transition-colors truncate pr-2">
                            {occasion.title}
                          </h4>
                          <span className={`flex-shrink-0 text-xs font-medium px-2 py-1 rounded-full bg-${occasion.color}-100 text-${occasion.color}`}>
                            {occasion.daysLeft === 0 ? 'Today' : `${occasion.daysLeft}d`}
                          </span>
                        </div>
                        <p className="text-sm text-accent-600 mb-2 truncate">
                          {occasion.date} • {occasion.relationship}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 text-xs text-accent-500">
                            <span>{occasion.suggestions} suggestions</span>
                            <span>•</span>
                            <span>{occasion.budget}</span>
                          </div>
                          <Icon name="ArrowRight" size={14} className="text-accent-400 group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="Calendar" size={48} className="text-accent-300 mx-auto mb-4" />
                  <h4 className="font-semibold text-accent-800 mb-2">No Upcoming Occasions</h4>
                  <p className="text-accent-600 mb-4">
                    {isAuthenticated ? 'Add your important dates to get started' : 'Sign in to add your important dates and get personalized gift suggestions'}
                  </p>
                  {isAuthenticated && (
                    <Button
                      variant="primary"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => navigate('/occasions')}
                    >
                      Add Occasion
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Occasion Types */}
          <div className="bg-white rounded-2xl shadow-soft border border-accent-100 overflow-hidden">
            <div className="p-6 border-b border-accent-100">
              <h3 className="font-heading text-xl font-semibold text-accent-800">
                Occasion Types
              </h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {occasionTypes.map((type, index) => (
                  <div
                    key={index}
                    onClick={() => navigate(isAuthenticated ? '/occasions' : '/login', {
                      state: isAuthenticated ? { category: type.name.toLowerCase() } : { from: '/occasions' }
                    })}
                    className="flex items-center justify-between p-4 rounded-xl hover:bg-surface transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 bg-${type.color}-100 rounded-lg flex items-center justify-center`}>
                        <Icon name={type.icon} size={20} className={`text-${type.color}`} />
                      </div>
                      <div>
                        <h4 className="font-medium text-accent-800 group-hover:text-primary transition-colors">
                          {type.name}
                        </h4>
                        <p className="text-sm text-accent-500">
                          {isAuthenticated ? `${type.count} upcoming` : 'Popular occasions'}
                        </p>
                      </div>
                    </div>
                    <Icon name="ArrowRight" size={16} className="text-accent-400 group-hover:text-primary transition-colors" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions - Only show for authenticated users */}
        {isAuthenticated && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="primary"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={handleViewAllOccasions}
              className="shadow-warm"
            >
              View All Occasions
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Plus"
              iconPosition="left"
              onClick={() => navigate('/occasions/create')}
              className="border-accent-200 hover:border-primary-300 hover:bg-primary-50"
            >
              Add New Occasion
            </Button>
          </div>
        )}

        {/* Call to Action for non-authenticated users */}
        {!isAuthenticated && (
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 text-center">
            <Icon name="Calendar" size={48} className="text-primary mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-accent-800 mb-4">
              Start Managing Your Special Moments
            </h3>
            <p className="text-accent-600 mb-6 max-w-2xl mx-auto">
              Sign up to track your important dates, get personalized gift recommendations,
              and never miss another special occasion.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="primary"
                size="lg"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => navigate('/register')}
              >
                Create Free Account
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="LogIn"
                iconPosition="left"
                onClick={() => navigate('/login')}
                className="border-primary-300 text-primary hover:bg-primary-50"
              >
                Sign In
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OccasionsSection;